import React, { useState } from "react"
import { Link, Navigate, useParams } from "react-router-dom"
import moment from "moment"

import CatchCard from "~/library/CatchCard"
import CatchTable from "~/library/CatchTable"
import LoadingWrapper from "~/library/LoadingWrapper"
import PageTitle from "~/library/PageTitle"
import useFetchResource from "~/hooks/useFetchResource"
import { apiWorkspaceStripeConnectedPayouts } from "~/routes"
import getAppConfig from "~/utils/getAppConfig"
import { formatCurrency, titleize } from "~/utils/helpers"
import { useToast } from "~/utils/Toast"
import type {
  StripeConnectedPayout,
  StripeConnectedPayoutEntry,
} from "~/types/StripeConnectedPayout"

type PayoutResponse = StripeConnectedPayout & { entries: StripeConnectedPayoutEntry[] }

const PayoutView = () => {
  const { id } = useParams<{ id: string }>()
  const { user, venueSlug } = getAppConfig()
  const toast = useToast()
  const [isRefreshing, setIsRefreshing] = useState(false)
  const endpoint = id ? apiWorkspaceStripeConnectedPayouts.show.path({ venueSlug, id }) : ""
  const { data: payout, isLoading, refetch } = useFetchResource<PayoutResponse>(endpoint)

  if (user.role !== "internal" || !id) return <Navigate to="/payments" replace />
  if (isLoading || !payout) return <LoadingWrapper isLoading />

  const refresh = async () => {
    setIsRefreshing(true)
    try {
      const response = await fetch(
        apiWorkspaceStripeConnectedPayouts.refresh.path({ venueSlug, id }),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      )
      if (!response.ok) throw new Error((await response.json()).errors?.[0] || "Refresh failed")

      toast("Payout synchronization queued", "success")
      window.setTimeout(() => refetch(), 1_000)
    } catch (error) {
      toast(error instanceof Error ? error.message : "Refresh failed", "danger")
    } finally {
      setIsRefreshing(false)
    }
  }

  const columns = [
    {
      key: "payment" as const,
      header: "Related Payment",
      formatter: (payment: StripeConnectedPayoutEntry["payment"]) =>
        payment ? (
          <Link to={`/payments/${payment.id}`}>INV-{payment.id.slice(0, 6).toUpperCase()}</Link>
        ) : (
          "Unmatched"
        ),
    },
    {
      key: "relatedInvoiceId" as const,
      header: "Related Invoice",
      formatter: (invoiceId: string | null) => invoiceId || "—",
    },
    {
      key: "amountCents" as const,
      header: "Gross",
      formatter: (value: number, entry: StripeConnectedPayoutEntry) =>
        formatCurrency({ cents: value, currency_iso: entry.currency }),
    },
    {
      key: "processingFeeCents" as const,
      header: "Processing Fee",
      formatter: (value: number, entry: StripeConnectedPayoutEntry) =>
        formatCurrency({ cents: value, currency_iso: entry.currency }),
    },
    {
      key: "netAmountCents" as const,
      header: "Net Proceeds",
      formatter: (value: number, entry: StripeConnectedPayoutEntry) =>
        formatCurrency({ cents: value, currency_iso: entry.currency }),
    },
    {
      key: "reconciliationStatus" as const,
      header: "Reconciliation",
      formatter: (value: string) => titleize(value),
    },
  ]

  return (
    <LoadingWrapper isLoading={false}>
      <PageTitle title={`Payout ${payout.stripePayoutId}`}>
        <div className="d-flex gap-2">
          <Link className="btn btn-outline-secondary" to="/payments/payouts">
            Back to Payouts
          </Link>
          <button
            type="button"
            className="btn btn-primary"
            onClick={refresh}
            disabled={isRefreshing}
          >
            {isRefreshing ? "Synchronizing…" : "Refresh from Stripe"}
          </button>
        </div>
      </PageTitle>

      <CatchCard title="Payout Details">
        <p className="text-muted">
          {payout.automatic === false
            ? "Stripe does not provide a payment-level breakdown for manual or instant payouts."
            : "The Stripe payout amount is the venue's complete bank payout; Catch fee and net proceeds include only matched Catch payments."}
        </p>
        <div className="row">
          <div className="col-md-2">
            <strong>Payout date</strong>
            <br />
            {payout.arrivalDate ? moment(payout.arrivalDate).format("MMM Do YYYY") : "—"}
          </div>
          <div className="col-md-2">
            <strong>Status</strong>
            <br />
            {titleize(payout.status)}
          </div>
          <div className="col-md-3">
            <strong>Stripe payout amount</strong>
            <br />
            {formatCurrency({ cents: payout.amountCents, currency_iso: payout.currency })}
          </div>
          <div className="col-md-2">
            <strong>Catch processing fee</strong>
            <br />
            {payout.automatic === false
              ? "—"
              : formatCurrency({ cents: payout.processingFeeCents, currency_iso: payout.currency })}
          </div>
          <div className="col-md-3">
            <strong>Net Catch proceeds</strong>
            <br />
            {payout.automatic === false
              ? "—"
              : formatCurrency({ cents: payout.netAmountCents, currency_iso: payout.currency })}
          </div>
        </div>
      </CatchCard>

      <CatchCard title="Included Catch Payments">
        <CatchTable
          data={payout.entries || []}
          columns={columns}
          emptyMessage={
            payout.automatic === false
              ? "Payment breakdown unavailable for manual payout"
              : "No Catch payment entries"
          }
          rowKey={(entry) => entry.id}
        />
      </CatchCard>
    </LoadingWrapper>
  )
}

export default PayoutView
